/**
 * Copyright IBM Corp. 2016, 2022
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, defineProperty as _defineProperty, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CaretDown } from '@carbon/icons-react';
import cx from 'classnames';
import uniqueId from '../../tools/uniqueId.js';
import { usePrefix } from '../../internal/usePrefix.js';
import { matches, match } from '../../internal/keyboard/match.js';
import { ArrowLeft, ArrowRight, Enter, Space } from '../../internal/keyboard/keys.js';

var _excluded = ["active", "children", "className", "depth", "disabled", "isExpanded", "label", "onNodeFocusEvent", "onSelect", "onToggle", "onTreeSelect", "renderIcon", "selected", "value"];
function TreeNode(_ref) {
  var _classNames, _objectSpread2$1;

  var active = _ref.active,
      children = _ref.children,
      className = _ref.className,
      depth = _ref.depth,
      disabled = _ref.disabled,
      isExpanded = _ref.isExpanded,
      label = _ref.label,
      onNodeFocusEvent = _ref.onNodeFocusEvent,
      onNodeSelect = _ref.onSelect,
      onToggle = _ref.onToggle,
      onTreeSelect = _ref.onTreeSelect,
      Icon = _ref.renderIcon,
      selected = _ref.selected,
      value = _ref.value,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useRef = useRef(rest.id || uniqueId()),
      id = _useRef.current;

  var _useState = useState(isExpanded),
      _useState2 = _slicedToArray(_useState, 2),
      expanded = _useState2[0],
      setExpanded = _useState2[1];

  var currentNode = useRef(null);
  var currentNodeLabel = useRef(null);
  var prefix = usePrefix();
  var nodesWithProps = React__default.Children.map(children, function (node) {
    if ( /*#__PURE__*/React__default.isValidElement(node)) {
      return /*#__PURE__*/React__default.cloneElement(node, {
        active: active,
        depth: depth + 1,
        disabled: disabled,
        onTreeSelect: onTreeSelect,
        selected: selected,
        tabIndex: !node.props.disabled && -1 || null
      });
    }
  });
  var isActive = active === id;
  var isSelected = selected.includes(id);
  var treeNodeClasses = cx(className, "".concat(prefix, "--tree-node"), (_classNames = {}, _defineProperty(_classNames, "".concat(prefix, "--tree-node--active"), isActive), _defineProperty(_classNames, "".concat(prefix, "--tree-node--disabled"), disabled), _defineProperty(_classNames, "".concat(prefix, "--tree-node--selected"), isSelected), _defineProperty(_classNames, "".concat(prefix, "--tree-node--with-icon"), Icon), _defineProperty(_classNames, "".concat(prefix, "--tree-leaf-node"), !children), _defineProperty(_classNames, "".concat(prefix, "--tree-parent-node"), children), _classNames));
  var toggleClasses = cx("".concat(prefix, "--tree-parent-node__toggle-icon"), _defineProperty({}, "".concat(prefix, "--tree-parent-node__toggle-icon--expanded"), expanded));

  function handleToggleClick(event) {
    if (disabled) {
      return;
    }

    onToggle === null || onToggle === void 0 ? void 0 : onToggle(event, {
      id: id,
      isExpanded: !expanded,
      label: label,
      value: value
    });
    setExpanded(!expanded);
  }

  function handleClick(event) {
    event.stopPropagation();

    if (!disabled) {
      var _rest$onClick;

      onTreeSelect === null || onTreeSelect === void 0 ? void 0 : onTreeSelect(event, {
        id: id,
        label: label,
        value: value
      });
      onNodeSelect === null || onNodeSelect === void 0 ? void 0 : onNodeSelect(event, {
        id: id,
        label: label,
        value: value
      });
      rest === null || rest === void 0 ? void 0 : (_rest$onClick = rest.onClick) === null || _rest$onClick === void 0 ? void 0 : _rest$onClick.call(rest, event);
    }
  }

  function handleKeyDown(event) {
    var _rest$onKeyDown;

    if (disabled) {
      return;
    }

    if (matches(event, [ArrowLeft, ArrowRight, Enter])) {
      event.stopPropagation();
    }

    if (match(event, ArrowLeft)) {
      var findParentTreeNode = function findParentTreeNode(node) {
        if (node.classList.contains("".concat(prefix, "--tree-parent-node"))) {
          return node;
        }

        if (node.classList.contains("".concat(prefix, "--tree"))) {
          return null;
        }

        return findParentTreeNode(node.parentNode);
      };

      if (children && expanded) {
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(event, {
          id: id,
          isExpanded: false,
          label: label,
          value: value
        });
        setExpanded(false);
      } else {
        var _findParentTreeNode;

        /**
         * When focus is on a leaf node or a closed parent node, move focus to
         * its parent node (unless its depth is level 1)
         */
        (_findParentTreeNode = findParentTreeNode(currentNode.current.parentNode)) === null || _findParentTreeNode === void 0 ? void 0 : _findParentTreeNode.focus();
      }
    }

    if (children && match(event, ArrowRight)) {
      if (expanded) {
        /**
         * When focus is on an expanded parent node, move focus to the first
         * child node
         */
        currentNode.current.lastChild.firstChild.focus();
      } else {
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(event, {
          id: id,
          isExpanded: true,
          label: label,
          value: value
        });
        setExpanded(true);
      }
    }

    if (matches(event, [Enter, Space])) {
      event.preventDefault();
      handleClick(event);
    }

    rest === null || rest === void 0 ? void 0 : (_rest$onKeyDown = rest.onKeyDown) === null || _rest$onKeyDown === void 0 ? void 0 : _rest$onKeyDown.call(rest, event);
  }

  function handleFocusEvent(event) {
    if (event.type === 'blur') {
      var _rest$onBlur;

      rest === null || rest === void 0 ? void 0 : (_rest$onBlur = rest.onBlur) === null || _rest$onBlur === void 0 ? void 0 : _rest$onBlur.call(rest, event);
    }

    if (event.type === 'focus') {
      var _rest$onFocus;

      rest === null || rest === void 0 ? void 0 : (_rest$onFocus = rest.onFocus) === null || _rest$onFocus === void 0 ? void 0 : _rest$onFocus.call(rest, event);
    }

    onNodeFocusEvent === null || onNodeFocusEvent === void 0 ? void 0 : onNodeFocusEvent(event);
  }

  useEffect(function () {
    /**
     * Negative margin shifts node to align with the left side boundary of the
     * tree
     * Dynamically calculate padding to recreate tree node indentation
     * - parent nodes have (depth + 1rem) left padding
     * - leaf nodes have (depth + 2.5rem) left padding without icons (because
     *   of expand icon + spacing)
     * - leaf nodes have (depth + 2rem) left padding with icons (because of
     *   reduced spacing between the expand icon and the node icon + label)
     */
    var calcOffset = function calcOffset() {
      // parent node
      if (children) {
        return depth + 1;
      } // leaf node with icon


      if (Icon) {
        return depth + 2;
      } // leaf node without icon


      return depth + 2.5;
    };

    if (currentNodeLabel.current) {
      currentNodeLabel.current.style.marginLeft = "-".concat(calcOffset(), "rem");
      currentNodeLabel.current.style.paddingLeft = "".concat(calcOffset(), "rem");
    } // sync props and state


    setExpanded(isExpanded);
  }, [children, depth, Icon, isExpanded]);

  var treeNodeProps = _objectSpread2(_objectSpread2({}, rest), {}, (_objectSpread2$1 = {}, _defineProperty(_objectSpread2$1, 'aria-current', isActive || null), _defineProperty(_objectSpread2$1, 'aria-selected', disabled ? null : isSelected), _defineProperty(_objectSpread2$1, 'aria-disabled', disabled), _defineProperty(_objectSpread2$1, "className", treeNodeClasses), _defineProperty(_objectSpread2$1, "id", id), _defineProperty(_objectSpread2$1, "onBlur", handleFocusEvent), _defineProperty(_objectSpread2$1, "onClick", handleClick), _defineProperty(_objectSpread2$1, "onFocus", handleFocusEvent), _defineProperty(_objectSpread2$1, "onKeyDown", handleKeyDown), _defineProperty(_objectSpread2$1, "ref", currentNode), _defineProperty(_objectSpread2$1, "role", 'treeitem'), _objectSpread2$1));

  if (!children) {
    return /*#__PURE__*/React__default.createElement("li", treeNodeProps, /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(prefix, "--tree-node__label"),
      ref: currentNodeLabel
    }, Icon && /*#__PURE__*/React__default.createElement(Icon, {
      className: "".concat(prefix, "--tree-node__icon")
    }), label));
  }

  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    React__default.createElement("li", _extends({}, treeNodeProps, {
      "aria-expanded": !!expanded
    }), /*#__PURE__*/React__default.createElement("div", {
      className: "".concat(prefix, "--tree-node__label"),
      ref: currentNodeLabel
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "".concat(prefix, "--tree-parent-node__toggle"),
      disabled: disabled,
      onClick: handleToggleClick
    }, /*#__PURE__*/React__default.createElement(CaretDown, {
      className: toggleClasses
    })), /*#__PURE__*/React__default.createElement("span", {
      className: "".concat(prefix, "--tree-node__label__details")
    }, Icon && /*#__PURE__*/React__default.createElement(Icon, {
      className: "".concat(prefix, "--tree-node__icon")
    }), label)), expanded && /*#__PURE__*/React__default.createElement("ul", {
      role: "group",
      className: "".concat(prefix, "--tree-node__children")
    }, nodesWithProps))
  );
}
TreeNode.propTypes = {
  /**
   * The value of the active node in the tree
   */
  active: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Specify the children of the TreeNode
   */
  children: PropTypes.node,

  /**
   * Specify an optional className to be applied to the TreeNode
   */
  className: PropTypes.string,

  /**
   * TreeNode depth to determine spacing, automatically calculated by default
   */
  depth: PropTypes.number,

  /**
   * Specify if the TreeNode is disabled
   */
  disabled: PropTypes.bool,

  /**
   * Specify if the TreeNode is expanded (only applicable to parent nodes)
   */
  isExpanded: PropTypes.bool,

  /**
   * Rendered label for the TreeNode
   */
  label: PropTypes.node,

  /**
   * Callback function for when the node receives or loses focus
   */
  onNodeFocusEvent: PropTypes.func,

  /**
   * Callback function for when the node is selected
   */
  onSelect: PropTypes.func,

  /**
   * Callback function for when a parent node is expanded or collapsed
   */
  onToggle: PropTypes.func,

  /**
   * Callback function for when any node in the tree is selected
   */
  onTreeSelect: PropTypes.func,

  /**
   * Optional prop to allow each node to have an associated icon.
   * Can be a React component class
   */
  renderIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

  /**
   * Array containing all selected node IDs in the tree
   */
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),

  /**
   * Specify the value of the TreeNode
   */
  value: PropTypes.string
};

export { TreeNode as default };
